import React from 'react';
import { QUESTIONS } from '@app/constants/questions';
import SpeakingReadAloud from './Speaking/Question/SpeakingReadAloud';
import { SpeakingRepeatSentence } from './Speaking/Question/SpeakingRepeatSentence';
import { IExam } from '@app/api/academic.api';
import { SpeakingDescribeImage } from './Speaking/Question/SpeakingDescribeImage';
import { SpeakingRetellLecture } from './Speaking/Question/SpeakingRetellLecture';
import { WritingSummarizeWrittenText } from './Writing/Question/WritingSummarizeWrittenText';
import { ReadingMCQRSingleAnswer } from './Reading/Question/ReadingMCQRSingleAnswer';
import { ReadingMCQRMultipleAnswer } from './Reading/Question/ReadingMCQRMultipleAnswer';
import { ReadingFIBR } from './Reading/Question/ReadingFIBR';
import { ReadingFIBRW } from './Reading/Question/ReadingFIBRW';
import { ReadingReOrderParagraph } from './Reading/Question/ReadingReOrderParagraph';
import { ListeningDictation } from './Listening/Question/ListeningDictation';
import { ListeningMCQRSingleAnswer } from './Listening/Question/ListeningMCQRSingleAnswer';
import { ListeningFIBL } from './Listening/Question/ListeningFIBL';
import { ListeningMCQLMultipleAnswer } from './Listening/Question/ListeningMCQLMultipleAnswer';
import { ListeningSummarizeSpokenText } from './Listening/Question/ListeningSummarizeSpokenText';
import { ListeningHighlightIncorectWord } from './Listening/Question/ListeningHighlightIncorectWord';

export interface RenderQuestionProps {
  type: string;
  currentQuestion: IExam | null;
  showAnswer: boolean;
  isFirstRender: React.MutableRefObject<boolean>;
  canStart: boolean;
  allowDrag: boolean;
  onAnswerChange?: (answer: string | Blob) => void;
  allowClipboardActions: boolean;
}

export const RenderQuestion: React.FC<RenderQuestionProps> = React.memo(
  ({
    type,
    currentQuestion,
    showAnswer,
    isFirstRender,
    canStart,
    allowDrag,
    onAnswerChange,
    allowClipboardActions,
  }) => {
    if (!currentQuestion) return null;
    const { SPEAKING, WRITING, READING, LISTENING } = QUESTIONS;

    switch (type) {
      case SPEAKING.READ_ALOUD:
        return (
          <SpeakingReadAloud
            waitingTime={40}
            recordingTime={40}
            showAnswer={showAnswer}
            text={currentQuestion.text}
            questionNumber={currentQuestion.id}
            canStart={canStart}
            onAnswerChange={onAnswerChange}
          />
        );
      case SPEAKING.REPEAT_SENTENCE:
        return (
          <SpeakingRepeatSentence
            prepTime={2}
            recordingTime={15}
            audioQuestion={currentQuestion?.audioLink}
            showAnswer={showAnswer}
            questionNumber={currentQuestion.id}
            isFirstRender={isFirstRender}
            gradingContent={currentQuestion.answer}
            canStart={canStart}
            allowDrag={allowDrag}
            onAnswerChange={onAnswerChange}
          />
        );
      case SPEAKING.DESCRIBE_IMAGE:
        return (
          <SpeakingDescribeImage
            prepTime={25}
            recordingTime={40}
            showAnswer={showAnswer}
            imageUrl={currentQuestion.audioLink}
            questionNumber={currentQuestion.id}
            canStart={canStart}
            onAnswerChange={onAnswerChange}
          />
        );
      case SPEAKING.RETELL_LECTURE:
        return (
          <SpeakingRetellLecture
            prepTime={3}
            recordingTime={40}
            waitingTime={10}
            audioQuestion={currentQuestion?.audioLink}
            showAnswer={showAnswer}
            questionNumber={currentQuestion.id}
            isFirstRender={isFirstRender}
            gradingContent={currentQuestion.answer}
            canStart={canStart}
            allowDrag={allowDrag}
            onAnswerChange={onAnswerChange}
          />
        );
      case SPEAKING.ANSWER_SHORT_QUESTION:
        return (
          <SpeakingRetellLecture
            prepTime={2}
            recordingTime={10}
            waitingTime={1}
            audioQuestion={currentQuestion?.audioLink}
            showAnswer={showAnswer}
            questionNumber={currentQuestion.id}
            isFirstRender={isFirstRender}
            gradingContent={currentQuestion.answer}
            canStart={canStart}
            allowDrag={allowDrag}
            onAnswerChange={onAnswerChange}
          />
        );
      case WRITING.SUMMARIZE_WRITTEN_TEXT:
        return (
          <WritingSummarizeWrittenText
            showAnswer={showAnswer}
            text={currentQuestion.text}
            questionNumber={currentQuestion.id}
            totalText={75}
            onAnswerChange={onAnswerChange}
            allowClipboardActions={allowClipboardActions}
          />
        );
      case WRITING.WRITING_ESSAY:
        return (
          <WritingSummarizeWrittenText
            showAnswer={showAnswer}
            text={currentQuestion.text}
            questionNumber={currentQuestion.id}
            totalText={300}
            onAnswerChange={onAnswerChange}
            allowClipboardActions={allowClipboardActions}
          />
        );
      case READING.MCQ_R_SINGLE_ANSWER:
        return (
          <ReadingMCQRSingleAnswer
            showAnswer={showAnswer}
            text={currentQuestion.text}
            description={currentQuestion.description}
            initialChoicesString={currentQuestion.oldChoices}
            answer={currentQuestion.answer}
            onAnswerChange={onAnswerChange}
            questionNumber={currentQuestion.id}
          />
        );
      case READING.MCQ_R_MULTIPLE_ANSWER:
        return (
          <ReadingMCQRMultipleAnswer
            showAnswer={showAnswer}
            text={currentQuestion.text}
            description={currentQuestion.description}
            initialChoicesString={currentQuestion.oldChoices}
            correctAnswersString={currentQuestion.answer}
            questionNumber={currentQuestion.id}
            onAnswerChange={onAnswerChange}
          />
        );
      case READING.FIB_R:
        return (
          <ReadingFIBR
            showAnswer={showAnswer}
            text={currentQuestion.text}
            initialChoicesString={currentQuestion.oldChoices}
            correctAnswersString={currentQuestion.answer}
            questionNumber={currentQuestion.id}
            onAnswerChange={onAnswerChange}
          />
        );
      case READING.FIB_R_W:
        return (
          <ReadingFIBRW
            showAnswer={showAnswer}
            text={currentQuestion.text}
            initialChoicesString={currentQuestion.oldChoices}
            correctAnswersString={currentQuestion.answer}
            questionNumber={currentQuestion.id}
            onAnswerChange={onAnswerChange}
          />
        );
      case READING.RE_ORDER_PARAGRAPH:
        return (
          <ReadingReOrderParagraph
            showAnswer={showAnswer}
            initialChoicesString={currentQuestion.oldChoices}
            correctAnswersString={currentQuestion.answer}
            onAnswerChange={onAnswerChange}
            questionNumber={currentQuestion.id}
          />
        );
      case LISTENING.DICTATION:
        return (
          <ListeningDictation
            prepTime={7}
            audioQuestion={currentQuestion.audioLink}
            audioAnswer={currentQuestion.audioLink}
            showAnswer={showAnswer}
            questionNumber={currentQuestion.id}
            isFirstRender={isFirstRender}
            gradingContent={currentQuestion.answer}
            allowDrag={allowDrag}
            onAnswerChange={onAnswerChange}
            canStart={canStart}
            allowClipboardActions={allowClipboardActions}
          />
        );
      case LISTENING.HIGHLIGHT_CORRECT_SUMMARY:
        return (
          <ListeningMCQRSingleAnswer
            prepTime={10}
            audioQuestion={currentQuestion.audioLink}
            showAnswer={showAnswer}
            questionNumber={currentQuestion.id}
            initialChoicesString={currentQuestion.oldChoices}
            correctAnswersString={currentQuestion.answer}
            isFirstRender={isFirstRender}
            allowDrag={allowDrag}
            onAnswerChange={onAnswerChange}
            canStart={canStart}
          />
        );
      case LISTENING.MCQ_L_SINGLE_ANSWER:
      case LISTENING.SELECT_MISSING_WORD:
        return (
          <ListeningMCQRSingleAnswer
            prepTime={7}
            audioQuestion={currentQuestion.audioLink}
            showAnswer={showAnswer}
            questionNumber={currentQuestion.id}
            initialChoicesString={currentQuestion.oldChoices}
            correctAnswersString={currentQuestion.answer}
            isFirstRender={isFirstRender}
            allowDrag={allowDrag}
            onAnswerChange={onAnswerChange}
            canStart={canStart}
          />
        );
      case LISTENING.FIB_L:
        return (
          <ListeningFIBL
            prepTime={7}
            audioQuestion={currentQuestion.audioLink}
            showAnswer={showAnswer}
            description={currentQuestion.description}
            correctAnswersString={currentQuestion.answer}
            isFirstRender={isFirstRender}
            questionNumber={currentQuestion.id}
            allowDrag={allowDrag}
            onAnswerChange={onAnswerChange}
            canStart={canStart}
          />
        );
      case LISTENING.MCQ_L_MULTIPLE_ANSWER:
        return (
          <ListeningMCQLMultipleAnswer
            prepTime={7}
            audioQuestion={currentQuestion.audioLink}
            showAnswer={showAnswer}
            initialChoicesString={currentQuestion.oldChoices}
            correctAnswersString={currentQuestion.answer}
            questionNumber={currentQuestion.id}
            isFirstRender={isFirstRender}
            allowDrag={allowDrag}
            onAnswerChange={onAnswerChange}
            canStart={canStart}
          />
        );

      case LISTENING.SUMMARIZE_SPOKEN_TEXT:
        return (
          <ListeningSummarizeSpokenText
            prepTime={12}
            audioQuestion={currentQuestion.audioLink}
            showAnswer={showAnswer}
            questionNumber={currentQuestion.id}
            isFirstRender={isFirstRender}
            allowDrag={allowDrag}
            onAnswerChange={onAnswerChange}
            canStart={canStart}
            allowClipboardActions={allowClipboardActions}
          />
        );
      case LISTENING.HIGHLIGHT_INCORRECT_WORD:
        return (
          <ListeningHighlightIncorectWord
            prepTime={10}
            audioQuestion={currentQuestion.audioLink}
            showAnswer={showAnswer}
            description={currentQuestion.description}
            correctAnswersString={currentQuestion.answer}
            questionNumber={currentQuestion.id}
            isFirstRender={isFirstRender}
            allowDrag={allowDrag}
            onAnswerChange={onAnswerChange}
            canStart={canStart}
          />
        );
      default:
        return null;
    }
  },

  (prevProps, nextProps) => {
    return (
      prevProps.type === nextProps.type &&
      prevProps.currentQuestion === nextProps.currentQuestion &&
      prevProps.showAnswer === nextProps.showAnswer &&
      prevProps.isFirstRender === nextProps.isFirstRender
    );
  },
);
